import React, { useState, useRef, useEffect } from "react";
import {
  Typography,
  CircularProgress,
  Grid,
  Box,
  Container,
  Autocomplete,
  TextField,
} from "@mui/material";
import api from "../../globals/axios";
import { CameraAlt, InsertPhoto } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  CustomLabel,
  CustomTitle,
  CustomValue,
} from "../../components/Custom/CustomTypography";
import CropHealth from "../../assets/Icon/CropHealth.png";
import GenericButton from "../../components/Custom/GenericButton";
import CustomAppBar from "../../components/AppBar";
import { useAlert } from "../../components/Alert";
import CropSelectionPopUp from "../../components/Advisory/CropSelectionPopUp";

const NutritionDeficiencyDetection = () => {
  const [imageFile, setImageFile] = useState(null);
  const [imageUploadFile, setImageUploadFile] = useState(null);
  const [deficiencies, setDeficiencies] = useState([]);

  const [description, setDescription] = useState("");
  const [plantName, setPlantName] = useState("");
  const [recommendations, setRecommendations] = useState([]);
  const [commodity, setCommodity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [symptoms, setSymptoms] = useState([]);
  const fileInputRef = useRef(null);
  const [confidenceLevel, setConfidenceLevel] = useState("");
  const [results, setResults] = useState({});
  const { t, i18n } = useTranslation();
  const { showAlert } = useAlert();

  const [showCropPopup, setShowCropPopup] = useState(false);
  const [predictionId, setPredictionId] = useState(null);

  useEffect(() => {
    i18n.changeLanguage("en");
  }, []);

  const handleGalleryUpload = () => {
    fileInputRef.current.click();
  };

  const handleUpload = async () => {
    // Validation
    if (!imageUploadFile) {
      alert(t("Please upload an image"));
      return;
    }
    if (!plantName) {
      alert(t("Please select a crop type"));
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("image", imageUploadFile);
    formData.append("plant_name", plantName);

    try {
      const response = await api.post("/ai/predict/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setResults(response.data.data.result.analysis);
      setPredictionId(response.data.data.prediction_id);
      setTimeout(() => {
        setShowCropPopup(true);
      }, 5000);
    } catch (error) {
      console.error("Error uploading image:", error);
      showAlert("error", t("Unable to process image. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const languageMap = {
    en: "english",
    hi: "hindi",
    mr: "marathi",
  };

  const handleAnalysisResults = () => {
    const language = languageMap[i18n.language];

    if (!results || Object.keys(results).length === 0) return;

    const languageData = results[language];
    if (!languageData) return;

    setDeficiencies(languageData.nutritional_deficiencies);
    setDescription(languageData.detailed_analysis);
    setRecommendations(languageData.recommendations);
    setConfidenceLevel(languageData.confidence_level);
    setSymptoms(languageData.symptoms);
  };

  useEffect(() => {
    handleAnalysisResults();
  }, [results, i18n.language]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageUploadFile(file);
      setImageFile(URL.createObjectURL(file));
    }
  };

  const handleCameraUpload = () => {
    const cameraInput = document.createElement("input");
    cameraInput.type = "file";
    cameraInput.accept = "image/*";
    cameraInput.capture = "camera";
    cameraInput.onchange = handleImageUpload;
    cameraInput.click();
  };

  const fetchCropsList = async () => {
    try {
      const response = await api.get("/common/commodities/");
      setCommodity(response.data);
    } catch (error) {
      console.log("Error Fetching Commodities list:", error);
    }
  };

  useEffect(() => {
    fetchCropsList();
  }, []);

  const handlePopupClose = () => {
    setShowCropPopup(false);
  };

  return (
    <>
      <CustomAppBar />
      <Container maxwidth="xs">
        <CustomTitle>{t("Crop Care")}</CustomTitle>
        <Box>
          <Box mb={2} width="100%">
            <Autocomplete
              options={commodity}
              getOptionLabel={(option) => option.name || ""}
              onChange={(_, newValue) => setPlantName(newValue?.name || "")}
              renderInput={(params) => (
                <TextField {...params} label={t("Crop Type")} />
              )}
              sx={{ marginTop: "20px" }}
            />
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            mt={2}
            sx={{
              backgroundColor: "white",
              padding: "40px 20px",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            {imageFile ? (
              <img
                src={imageFile}
                alt="Selected"
                style={{
                  width: "100%",
                  maxHeight: "200px",
                  borderRadius: "8px",
                }}
              />
            ) : (
              <>
                <img src={CropHealth} alt="Upload" style={{ width: "100px" }} />
                <Typography variant="h6" mt={2}>
                  {t("Upload a Crop photo")}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t(
                    "You can upload a photo by importing or clicking with your camera"
                  )}
                </Typography>
              </>
            )}
          </Box>

          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={6} display="flex" justifyContent="center">
              <GenericButton
                padding="8px 0"
                borderColor="#A5A5A5"
                onClick={handleCameraUpload}
              >
                <CameraAlt fontSize="medium" sx={{ mr: 2, color: "#4CAF50" }} />
                {t("Camera")}
              </GenericButton>
            </Grid>
            <Grid item xs={6} display="flex" justifyContent="center">
              <GenericButton
                padding="8px 0"
                borderColor="#A5A5A5"
                onClick={handleGalleryUpload}
              >
                <InsertPhoto
                  fontSize="medium"
                  sx={{ mr: 2, color: "#4CAF50", fontSize: "28px" }}
                />
                {t("Gallery")}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                  ref={fileInputRef}
                />
              </GenericButton>
            </Grid>
          </Grid>
          <GenericButton
            padding="10px"
            backgroundColor="#4CAF50"
            fontColor="#fff"
            onClick={handleUpload}
            disabled={!imageFile || !plantName}
          >
            {t("Process")}
          </GenericButton>
        </Box>

        <Grid container mb={3}>
        <Grid container mb={3}>
          {loading ? (
            <Grid item xs={12} mt={2} display="flex" justifyContent="center">
              <CircularProgress />
            </Grid>
          ) : (
            deficiencies.length !== 0 && (
              <>
                <Grid container alignItems="center" mt={2}>
                  <Grid item>
                    <CustomLabel>{t("Nutrition Deficiency")} - </CustomLabel>
                    {deficiencies.map((deficiency, index) => (
                      <CustomValue key={index}>
                        {`${index + 1}.${deficiency}`}
                      </CustomValue>
                    ))}
                  </Grid>
                </Grid>
                <Grid container alignItems="center">
                  <Grid item>
                    <CustomLabel>{t("Confidence Level")} - </CustomLabel>
                    <CustomValue>{confidenceLevel}</CustomValue>
                  </Grid>
                </Grid>
                <Grid container alignItems="center">
                  <Grid item xs={12}>
                    <CustomLabel>{t("Symptoms")} - </CustomLabel>
                    {symptoms.map((step, index) => (
                      <CustomValue key={index}>
                        {`${index + 1}.${step}`}
                      </CustomValue>
                    ))}
                  </Grid>
                </Grid>
                <Grid container alignItems="center">
                  <Grid item>
                    <CustomLabel>{t("Description")} - </CustomLabel>
                    <CustomValue>{description}</CustomValue>
                  </Grid>
                </Grid>
                <Grid container alignItems="center">
                  <Grid item xs={12}>
                    <CustomLabel>{t("Recommendations")} - </CustomLabel>
                    {recommendations.map((recommendation, index) => (
                      <CustomValue key={index}>
                        {`${index + 1}.${recommendation}`}
                      </CustomValue>
                    ))}
                  </Grid>
                </Grid>
              </>
            )
          )}
        </Grid>

        </Grid>
        <CropSelectionPopUp
          open={showCropPopup}
          onClose={handlePopupClose}
          prediction_id={predictionId}
        />
      </Container>
    </>
  );
};

export default NutritionDeficiencyDetection;
